// clinic.store.ts
import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import {
  setAllEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { exhaustMap, pipe, switchMap, tap } from 'rxjs';
import { Clinic, DataStatus, StoreError } from '../models/models';
import { ClinicService } from '../../../core/services/clinic/clinic.service';

interface ClinicState {
  clinicStatus: DataStatus;
  activeClinicId: number;
  error: StoreError;
}

export const ClinicStore = signalStore(
  { providedIn: 'root', protectedState: false },

  withState<ClinicState>({
    clinicStatus: 'idle',
    activeClinicId: 0,
    error: { error: false, message: '' },
  }),

  withEntities<Clinic>(),

  withProps(() => ({
    adminService: inject(ClinicService),
  })),

  withComputed((store) => ({
    clinicList: computed(() => store.entities()),
    activeClinic: computed(() =>
      store.entities().find((c) => c.id === store.activeClinicId()) ?? null
    )
  })),
  withComputed((store) => ({
    locations: computed(() => store.activeClinic()?.locations ?? [])
  })),
  withMethods((store) => ({
    setClinicStatus(clinicStatus: DataStatus) {
      patchState(store, { clinicStatus });
    },

    setActiveClinicId(activeClinicId: number) {
      patchState(store, { activeClinicId });
    },

    setError(message: string) {
      patchState(store, {
        clinicStatus: 'error',
        error: { error: true, message },
      });
    },

    clearError() {
      patchState(store, {
        error: { error: false, message: '' },
      });
    },
  })),

  withMethods((store) => ({
    loadClinics: rxMethod<void>(
      pipe(
        tap(() => {
          store.clearError();
          store.setClinicStatus('loading');
        }),
        switchMap(() =>
          store.adminService.allClinics().pipe(
            tapResponse({
              next: (clinics: Clinic[]) => {
                patchState(store, setAllEntities(clinics));
                store.setClinicStatus('success');
              },
              error: (err: unknown) => {
                console.error(err);
                store.setError('Failed to load clinics.');
              },
            })
          )
        )
      )
    ),

    activateClinic: rxMethod<number>(
      pipe(
        tap(() => {
          store.clearError();
          store.setClinicStatus('loading');
        }),
        switchMap((clinic_id) =>
          store.adminService.activateClinic(clinic_id).pipe(
            tapResponse({
              next: (response: any) => {
                patchState(
                  store,
                  updateEntity({
                    id: response.clinic_id,
                    changes: {
                      active: response.active,
                    },
                  })
                );
                store.setClinicStatus('success');
              },
              error: (err: unknown) => {
                console.error(err);
                store.setError('Failed to activate clinic.');
                store.setClinicStatus('error')
              },
            })
          )
        )
      )
    ),
    saveDemographics: rxMethod<{dto: any, onSuccess?: () => void}>(
      pipe(
        tap(() => {
          store.clearError()
          store.setClinicStatus('loading')
        }),
        exhaustMap(({dto, onSuccess}) => 
          store.adminService.updateClinicDemographics(dto.clinic_id, dto.demographics).pipe(
            tapResponse({
              next: () => {
                patchState(store,
                  updateEntity({
                    id: dto.clinic_id,
                    changes: {
                      npi: dto.demographics.clinic_npi,
                      clinic_website: dto.demographics.clinic_website,
                    }
                }))
                store.setClinicStatus('success')
              },
              error: () => {
                store.setClinicStatus('error')
              }
            })
          )
        )
      )
    ),
    deactivateClinic: rxMethod<{clinic_id: number, onSuccess?: () => void}>(
      pipe(
        tap(() => {
          store.clearError()
          store.setClinicStatus('loading')
        }),
        exhaustMap(({clinic_id, onSuccess}) => 
          store.adminService.deactivateClinic(clinic_id).pipe(
            tapResponse({
              next: (response: any) => {
                patchState(
                  store, 
                  updateEntity({
                    id: clinic_id,
                    changes: {
                      active: false
                    }
                  })
                )
                store.setClinicStatus('success')
                onSuccess?.()
              },
              error: (error: unknown) => {
                console.error(error);
                store.setError('Failed to activate clinic.');
                store.setClinicStatus('error')
              }
            })
          )
        )
      )
    )
  }))
);